'use strict';

const get = require('lodash.get');

/**
 * Convenience method for setting data in the state
 *
 * @param {object} state
 * @param {object} data
 * @returns {object}
 */
function setData(state, data) {
    return Object.assign({}, state, {
        data: Object.assign({}, state.data, data),
    });
}

const GalleryModalStore = {
    storeName: 'GalleryModalStore',
    initialState: {
        isHidden: true,
        hasOpened: false,

        data: {
            template: void 0,
            originalUrl: void 0,
            originalVPVString: void 0,
            originalPageTitleTag: void 0,
            originalPageType: void 0,
            originalDataLayerInfo: void 0,
        },
    },

    reducers: {
        /**
         * Setup Gallery - sets the original url (the url of the page that renders the gallery modal)
         *
         * @param {Object} payload
         * @param {string} payload.originalUrl
         * @param {string} payload.originalVPVString
         * @param {string} payload.originalPageTitleTag
         * @param {string} payload.originalPageType
         * @param {string} payload.originalDataLayerInfo
         */
        ['gallery-modal:setup-store']: function setupGalleryModal(state, payload) {
            return setData(state, {
                originalUrl: payload.originalUrl,
                originalVPVString: payload.originalVPVString,
                originalPageTitleTag: payload.originalPageTitleTag,
                originalPageType: payload.originalPageType,
                originalDataLayerInfo: payload.originalDataLayerInfo,
            });
        },

        /**
         * Show Gallery - sets isHidden to false
         */
        ['gallery-modal:show']: function showGalleryModal(state) {
            if (state.isHidden) {
                return Object.assign({}, state, {
                    isHidden: false,
                });
            }
            return state;
        },

        /**
         * Hide Gallery - sets isHidden to true
         */
        ['gallery-modal:hide']: function hideGalleryModal(state) {
            if (!state.isHidden) {
                return Object.assign({}, state, {
                    isHidden: true,
                    hasOpened: true,
                });
            }
            return state;
        },

        /**
         * Set the template
         * @param {Object} payload
         * @param {string} payload.template
         */
        ['gallery-modal:fetched']: function setGalleryTemplate(state, payload) {
            return setData(state, {
                template: payload.template,
            });
        },

        /**
         * Reset the template
         */
        ['gallery-modal:reset']: function resetModal(state) {
            return Object.assign({}, state, {
                hasOpened: false,
                data: Object.assign({}, state.data, {
                    template: void 0,
                }),
            });
        },
    },

    getters: {
        /**
         * Checks if the modal is closed
         * @returns {boolean}
         */
        isHidden: state => state.isHidden,

        /**
         * Checks if the modal has been opened
         * @returns {boolean}
         */
        hasOpened: state => state.hasOpened,

        /**
         * Checks if the modal is open but has no template yet
         * @returns {boolean}
         */
        isLoading: state => !state.isHidden && !state.hasOpened && !get(state, 'data.template'),
        shouldFetchTemplate: state =>
            !state.isHidden && !state.hasOpened && !get(state, 'data.template'),

        /**
         * Checks if the template has just loaded
         * @returns {boolean}
         */
        hasJustLoaded: state =>
            !state.isHidden && !state.hasOpened && !!get(state, 'data.template'),

        /**
         * Returns the gallery template
         * @returns {string}
         */
        getTemplate: state => get(state, 'data.template', ''),

        /**
         * Returns the original url
         *  (e.g. the url of the page that opens the gallery modal)
         * @returns {string}
         */
        getOriginalUrl: state => get(state, 'data.originalUrl', ''),

        /**
         * Returns the original vpv string
         *  (e.g. the vpv string of the page that opens the gallery modal)
         * @returns {string}
         */
        getOriginalVPVString: state => get(state, 'data.originalVPVString', ''),

        /**
         * Returns the original page title tag
         *  (e.g. the text in the <title> tag of the page that opens the gallery modal)
         *  @returns {string}
         */
        getOriginalPageTitleTag: state => get(state, 'data.originalPageTitleTag', ''),

        /**
         * Returns the original page type
         *  (e.g. pdp, project, photos)
         *  @returns {string}
         */
        getOriginalPageType: state => get(state, 'data.originalPageType', ''),

        /**
         * Returns the original data layer object
         *  (e.g. pdp, project, photos)
         *  @returns {string}
         */
        getOriginalDataLayerInfo: state => get(state, 'data.originalDataLayerInfo', {}),
    },
};

module.exports = GalleryModalStore;



// WEBPACK FOOTER //
// ./assets/js/gallery/modal/GalleryModalStore.es.js